import { formatIQD, grandTotal, isItemFilled, lineTotal, ROWS_PER_PAGE, subtotal } from "../../data/price";
import { Box, Brand, LogoAt, Page, pt, Text } from "../primitives";
/**
 * «عرض السعر» pages. The «الو بيروت» file has no price page, so the design
 * comes from the Arabic master template (same family, 842.3 × 595.5 pt),
 * scaled onto the 1125 × 810 pt page. All template values are kept below in
 * template units so they can be traced back to the source.
 */
const SX = 1125 / 842.3;
const SY = 810 / 595.5;
const X = (v) => v * SX;
const Y = (v) => v * SY;
const F = (v) => v * SX;
// Item table columns, template x-boundaries, right → left.
const COLS = [
    { key: "total", label: "السعر الكلي ( دينار عراقي )", from: 648.3, to: 797.5 },
    { key: "unit", label: "سعر اللوحة ( دينار عراقي )", from: 525, to: 648.3 },
    { key: "qty", label: "العدد", from: 477.5, to: 525 },
    { key: "dims", label: "ابعاد اللوحة ( سم )", from: 283.3, to: 477.5 },
    { key: "frame", label: "إطار", from: 190.8, to: 283.3 },
    { key: "desc", label: "الوصف", from: 42.4, to: 190.8 },
];
const TABLE_LEFT = 42.4;
const TABLE_RIGHT = 797.5;
const HEADER_TOP = 194.2;
const HEADER_H = 37.6;
const ROW_H = 39.3;
const TOTAL_H = 41.2;
const GAP = 0.75; // white column gap (template units)
const CELL_FONT = F(10);
function Cell({ x, y, w, h, children, style, className }) {
    return (<Box x={x} y={y} w={w} h={h} className={`doc-cell ${className ?? ""}`} style={style}>
      {children}
    </Box>);
}
const Num = ({ value }) => <bdi dir="ltr">{value}</bdi>;
function PriceHeader({ offer }) {
    const metaRows = [
        { label: "التاريخ", value: offer.date },
        { label: "رقم الفاتوره", value: offer.quoteNumber },
        { label: "صالحة لغاية", value: offer.validUntil },
    ];
    const metaTop = Y(79.6);
    const metaRowH = Y(147.2 - 79.6) / 3;
    const metaLeft = X(42.4);
    const metaSplit = X(98.3);
    const metaRight = X(145.1);
    const info = [
        { label: "المشروع", value: offer.project, top: 113 },
        { label: "الموقع", value: offer.location, top: 125 },
        { label: "رقم التلفون", value: offer.phone, top: 138 },
    ];
    return (<>
      <LogoAt cx={73.4} cy={60.6} w={31.2}/>
      <Brand right={X(795)} top={Y(20)} size={F(25)}/>

      {/* date / quote no. / valid until */}
      {metaRows.map((row, i) => (<div key={row.label}>
          <Cell x={metaSplit} y={metaTop + i * metaRowH} w={metaRight - metaSplit} h={metaRowH} className="doc-meta-cell" style={{ justifyContent: "flex-start", paddingRight: pt(6) }}>
            <span style={{ fontSize: pt(F(8)) }}>{row.label}</span>
          </Cell>
          <Cell x={metaLeft} y={metaTop + i * metaRowH} w={metaSplit - metaLeft} h={metaRowH} className="doc-meta-cell" style={{ justifyContent: "flex-end", paddingLeft: pt(3) }}>
            <span style={{ fontSize: pt(F(8)) }}>
              <bdi>{row.value}</bdi>
            </span>
          </Cell>
        </div>))}

      {/* customer info */}
      <Cell x={X(618.9)} y={Y(79.6)} w={X(797.5 - 618.9)} h={Y(105.8 - 79.6)} style={{ background: "#000000", color: "#ffffff", justifyContent: "flex-start", paddingRight: pt(X(4.5)) }}>
        <span style={{ fontSize: pt(F(9)), fontWeight: 700 }}>معلومات الزبون</span>
      </Cell>
      {info.map((row) => (<Text key={row.label} right={X(796)} top={Y(row.top) - 3} size={F(9)} color="#000000">
          {row.label}: <bdi>{row.value}</bdi>
        </Text>))}
    </>);
}
function cellValue(key, item) {
    if (!item || !isItemFilled(item))
        return "";
    switch (key) {
        case "total":
            return <Num value={formatIQD(lineTotal(item))}/>;
        case "unit":
            return <Num value={formatIQD(item.unitPrice)}/>;
        case "qty":
            return <Num value={item.quantity}/>;
        case "dims":
            return <bdi>{item.dimensions}</bdi>;
        case "frame":
            return item.frame;
        case "desc":
            return item.description;
        default:
            return "";
    }
}
/** Items table page. Long lists continue on further pages; the subtotal row closes the last one. */
export function PriceItemsPage({ offer, items, isLast }) {
    const rows = [...items];
    while (rows.length < 4)
        rows.push(undefined); // the reference table always shows at least 4 rows
    const anyFilled = offer.items.some(isItemFilled);
    const bodyTop = HEADER_TOP + HEADER_H;
    const totalTop = bodyTop + rows.length * ROW_H;
    return (<Page>
      <PriceHeader offer={offer}/>

      {COLS.map((col) => (<Cell key={col.key} x={X(col.from + GAP / 2)} y={Y(HEADER_TOP)} w={X(col.to - col.from - GAP)} h={Y(HEADER_H)} style={{ background: "#000000", color: "#ffffff" }}>
          <span style={{ fontSize: pt(CELL_FONT) }}>{col.label}</span>
        </Cell>))}

      {rows.map((item, r) => COLS.map((col) => (<Cell key={`${r}-${col.key}`} x={X(col.from + GAP / 2)} y={Y(bodyTop + r * ROW_H)} w={X(col.to - col.from - GAP)} h={Y(ROW_H)} className="doc-item-cell" style={{ borderBottom: r === rows.length - 1 ? `${pt(1.1)} solid #000000` : undefined }}>
            <span className={col.key === "desc" ? "doc-clamp" : undefined} style={{ fontSize: pt(CELL_FONT) }}>
              {cellValue(col.key, item)}
            </span>
          </Cell>)))}

      {isLast && (<>
          <Cell x={X(TABLE_LEFT)} y={Y(totalTop)} w={X(648.3 - GAP / 2 - TABLE_LEFT)} h={Y(TOTAL_H)} style={{ background: "#231f20", color: "#ffffff", justifyContent: "flex-start", paddingRight: pt(X(8)) }}>
            <span style={{ fontSize: pt(CELL_FONT) }}>المجموع الاولي</span>
          </Cell>
          <Cell x={X(648.3 + GAP / 2)} y={Y(totalTop)} w={X(TABLE_RIGHT - 648.3 - GAP / 2)} h={Y(TOTAL_H)} style={{ background: "#231f20", color: "#ffffff" }}>
            <span style={{ fontSize: pt(CELL_FONT), fontWeight: 700 }}>{anyFilled ? <Num value={formatIQD(subtotal(offer))}/> : ""}</span>
          </Cell>
        </>)}
    </Page>);
}
const TERMS = [
    {
        title: "مدة صلاحية عرض السعر:",
        body: "هذا العرض صالح لمدة 30 يومًا من تاريخ الإصدار اعلاه. الأسعار قابلة للمراجعة بعد ذلك.",
    },
    {
        title: "الدفعة المقدمة:",
        body: "يتم تسديد الدفعات على النحو التالي: 50% دفعة مقدّمة عند بدء المشروع، و25% عند تسليم 50% من أعمال المشروع، و25% عند تسليم كامل أعمال المشروع.",
    },
    {
        title: "الجدول الزمني للإنتاج:",
        body: "سيتم تسليم العمل الفني خلال 30-45 يوم من تاريخ الاتفاق، حسب درجة التعقيد والنوع (أصلي، طبعة، رئيسي).",
    },
    {
        title: "التخصيص والموافقة:",
        body: "سيتم تنفيذ الأعمال الفنية المخصصة بناءً على المفهوم المُعتمد. أي تغييرات في التصميم مطلوبة بعد الموافقة قد تؤثر على التكلفة أو وقت التسليم.",
    },
];
const TERM_TITLE_TOPS = [346, 379, 412, 445];
const TERM_BODY_TOPS = [362, 395, 428, 461];
const BULLET_TOPS = [354.3, 387.3, 420.3, 453.4];
/** Totals + terms page (template page 22). Terms are fixed company terms. */
export function PriceTotalsPage({ offer }) {
    const anyFilled = offer.items.some(isItemFilled);
    const show = (v) => (anyFilled ? <Num value={formatIQD(v)}/> : "");
    // من فاتورة المشروع: رسوم التركيب تظهر بس إذا موجودة، والتوصيل ينخفي إذا
    // الفاتورة مخفيته عن الزبون (يبقى محسوب بالمجموع - نفس الفاتورة بالضبط)
    const rows = [
        { label: "المجموع الاولي", value: show(subtotal(offer)) },
        { label: "رسوم التصميم والاستشارات", value: show(offer.designFee) },
        !offer.hideDeliveryFee && { label: "رسوم التوصيل", value: show(offer.deliveryFee) },
        offer.installationFee > 0 && { label: "رسوم التركيب", value: show(offer.installationFee) },
        { label: "رسوم زيارة الموقع", value: show(offer.siteVisitFee) },
        { label: "تخفيض", value: show(offer.discount) },
        { label: "المجموع الكلي", value: show(grandTotal(offer)), strong: true },
    ].filter(Boolean);
    const top = 161.3;
    const rowH = (305.4 - 161.3) / rows.length;
    const split = 186.5;
    return (<Page>
      <PriceHeader offer={offer}/>

      <Box x={X(TABLE_LEFT)} y={Y(top)} w={X(TABLE_RIGHT - TABLE_LEFT)} h={Y(305.4 - top)} className="doc-totals">
        {rows.map((row, i) => (<div key={row.label} className="doc-totals-row" style={{ top: pt(Y(i * rowH)), height: pt(Y(rowH)) }}>
            <div className={`doc-totals-value ${row.strong ? "is-strong" : ""}`} style={{ width: pt(X(TABLE_RIGHT - split)), fontSize: pt(F(9)) }}>
              {row.value}
            </div>
            <div className="doc-totals-label" style={{ width: pt(X(split - TABLE_LEFT)), fontSize: pt(F(9)) }}>
              {row.label}
            </div>
          </div>))}
      </Box>

      <Cell x={X(618.9)} y={Y(319.1)} w={X(797.5 - 618.9)} h={Y(347.8 - 319.1)} style={{ background: "#000000", color: "#ffffff", justifyContent: "flex-start", paddingRight: pt(X(4.5)) }}>
        <span style={{ fontSize: pt(F(10)), fontWeight: 700 }}>الشروط والأحكام</span>
      </Cell>
      {TERMS.map((term, i) => (<div key={term.title}>
          <Box x={X(788.2)} y={Y(BULLET_TOPS[i])} w={X(2.2)} h={X(2.2)} style={{ background: "#000000" }}/>
          <Text right={X(780)} top={Y(TERM_TITLE_TOPS[i])} size={F(10)} weight={500} color="#000000">
            {term.title}
          </Text>
          <Text right={X(796.5)} top={Y(TERM_BODY_TOPS[i])} size={F(10)} weight={500} color="#000000">
            {term.body}
          </Text>
        </div>))}
    </Page>);
}
/** Splits the items over as many table pages as needed, then adds the totals page. */
export function PriceOfferPages({ offer, wrap }) {
    const chunks = [];
    for (let i = 0; i < offer.items.length; i += ROWS_PER_PAGE)
        chunks.push(offer.items.slice(i, i + ROWS_PER_PAGE));
    if (chunks.length === 0)
        chunks.push([]);
    return (<>
      {chunks.map((items, i) => wrap(<PriceItemsPage offer={offer} items={items} isLast={i === chunks.length - 1}/>, `items-${i}`))}
      {wrap(<PriceTotalsPage offer={offer}/>, "totals")}
    </>);
}
